<script setup lang="ts">
import BlockingLoading from '@/ui-plus/BlockingLoading.vue';
import { formatAmountForStripe, formatAmountFromStripe } from '@lib/utils/formatAmountForStripe';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-vue-next';
import { formatDate } from '@lib/utils/formatDate';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { ref, onMounted } from 'vue';
import { courierRequestState_perOrderRef } from './state';
import { toast } from '@/ui-plus/sonner';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];

const props = defineProps<{
    order: OrderDetailsType;
}>();

const emit = defineEmits<{
    close: [];
}>();

type UberDirectQuoteResponseType = ResType<typeof storeOrdersApiClient.uber_direct_quote[":storeId"]['$post']>["data"];
const uberQuote = ref<UberDirectQuoteResponseType | null>(null);

type UberDirectCreateDeliveryResponseType = ResType<typeof storeOrdersApiClient.uber_direct_create_delivery[":storeId"]['$post']>["data"];

type UberDirectCreateDeliveryRequestBody = Parameters<typeof storeOrdersApiClient.uber_direct_create_delivery[":storeId"]['$post']>[0]['json'];

const isLoading = ref(false);
const isCreatingDelivery = ref(false);

const state = courierRequestState_perOrderRef.value[props.order.storeOrderId];

async function getQuote() {
    if (!state.pickupAddress || !state.deliveryAddress) {
        toast.error('Pickup and delivery addresses are required');
        return;
    }
    isLoading.value = true;
    const { data, error } = await (await storeOrdersApiClient.uber_direct_quote[":storeId"].$post({
        param: { storeId: props.order.storeId },
        json: {
            pickup: state.pickupAddress,
            delivery: state.deliveryAddress,
        },
    })).json();
    isLoading.value = false;
    if (error) {
        toast.error(error.message ?? 'Failed to fetch Uber Direct quote');
        return;
    }
    uberQuote.value = data;
}

async function createDelivery() {
    if (!state.pickupAddress || !state.deliveryAddress) {
        toast.error('Pickup and delivery addresses are required');
        return;
    }
    if (!uberQuote.value?.id) {
        toast.error('Get a quote first');
        return;
    }
    const cleanedManifest = state.manifestItems
        .map((i) => ({ name: i.name.trim(), quantity: Number(i.quantity) || 0 }))
        .filter((i) => i.name.length > 0 && i.quantity > 0);
    if (cleanedManifest.length === 0) {
        toast.error('Add at least one manifest item (name + quantity)');
        return;
    }
    const tipAmountForApi = formatAmountForStripe(
        Math.max(0, Number(state.tipCourier) || 0), uberQuote.value?.currency_type ?? 'USD'
    );
    const createDeliveryPayload: UberDirectCreateDeliveryRequestBody = {
        storeOrderId: props.order.storeOrderId,
        shortOtp: props.order.shortOtp,
        pickup: state.pickupAddress,
        delivery: state.deliveryAddress,
        quote_id: uberQuote.value?.id,
        dropoff_seller_notes: state.dropoffSellerNotes.trim() || undefined,
        return_notes: state.returnNotes.trim() || undefined,
        undeliverable_action: state.undeliverableAction ?? 'return',
        manifest_items: cleanedManifest,
    };
    if (tipAmountForApi > 0) {
        (createDeliveryPayload as Record<string, unknown>).tip = tipAmountForApi;
    }
    isCreatingDelivery.value = true;
    try {
        const { data, error } = await (await storeOrdersApiClient.uber_direct_create_delivery[":storeId"].$post({
            param: { storeId: props.order.storeId },
            json: createDeliveryPayload,
        })).json();
        if (error) {
            toast.error(error.message ?? 'Failed to create Uber Direct delivery');
            return;
        }
        addToUI(data);
        // await new Promise(resolve => setTimeout(resolve, 2000));
        toast.success('Uber Direct delivery created');
        emit('close');
    } finally {
        isCreatingDelivery.value = false;
    }
}

function addToUI(delivery: UberDirectCreateDeliveryResponseType) {
    props.order.uberDirectDeliveries.push({
        id: delivery.id,
        created_at_ts: Date.now(),
        storeId: props.order.storeId,
        updated_at_ts: Date.now(),
        storeOrderId: props.order.storeOrderId,
        isCancelled: false,
        uberDeliveryId: delivery.id,
        uuid: delivery.uuid,
        quoteId: delivery.quote_id,
        trackingUrl: delivery.tracking_url ?? null,
        cancelReason: null,
    });
}


onMounted(() => {
    getQuote();
});

</script>

<template>
    <BlockingLoading v-if="isCreatingDelivery" />
    <div v-if="isLoading"
         class="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 class="h-4 w-4 animate-spin" />
        Loading quote...
    </div>
    <div v-else
         class="space-y-1 text-sm">
        <div class="font-bold">
            Total:
            {{ formatCurrency(formatAmountFromStripe(uberQuote?.fee ?? 0, uberQuote?.currency_type ?? 'USD') +
                Number(courierRequestState_perOrderRef[props.order.storeOrderId]?.tipCourier ?? 0), uberQuote?.currency_type ?? 'USD') }}
        </div>
        <div>Quote: <span
                  class="font-medium">{{ formatCurrency(formatAmountFromStripe(uberQuote?.fee ?? 0, uberQuote?.currency_type ?? 'USD'), uberQuote?.currency_type ?? 'USD') }}</span>
            +
            {{ formatCurrency(courierRequestState_perOrderRef[props.order.storeOrderId]?.tipCourier ?? 0, uberQuote?.currency_type ?? 'USD') }}
            tip
        </div>
        <div v-if="uberQuote?.duration">Duration: ~{{ uberQuote.duration }} min</div>
        <div v-if="uberQuote?.dropoff_eta">Dropoff ETA: {{ formatDate(
            new Date(uberQuote.dropoff_eta).getTime(),
            undefined,
            true
        ) }}
        </div>
        <div v-if="uberQuote?.expires"
             class="text-muted-foreground text-xs">
            Expires: {{ formatDate(
                new Date(uberQuote.expires).getTime(),
                undefined,
                true
            ) }}
        </div>
        <div class="text-xs text-muted-foreground">Quote ID: {{ uberQuote?.id }}</div>
        <div class="pt-6">
            <Button class="w-full"
                    variant="default"
                    :disabled="isCreatingDelivery"
                    @click="createDelivery">
                <Loader2 v-if="isCreatingDelivery"
                         class="animate-spin h-4 w-4 mr-2" />
                Create Delivery
            </Button>
            <div class="text-xs bg-destructive/50 text-destructive-foreground p-2 rounded-md mt-2">
                Once requested, your uber direct account will be charged for the delivery fee.
            </div>
        </div>
    </div>
</template>